import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { modalBackdropCls, modalCardCls, overlayCls } from './overlay';

// A small confirm/cancel modal, shared by the destructive actions that need a
// second look (deleting a generated video, emptying the parts database, …).
// Click the backdrop or press Escape to cancel.
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  danger = true,
}: {
  title: string;
  body?: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  // Red confirm button (for deletes); set false for a neutral confirm.
  danger?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // ON THE BODY, NOT WHERE IT IS WRITTEN. `fixed` is relative to the viewport only while no
  // ancestor has a transform, a filter or a backdrop blur — and the panels this is written in do,
  // so the question drew inside one of them, out of sight, and the button that asked it seemed to
  // do nothing (the keep switch of a product whose parts have holes drilled in them).
  return createPortal((
    <div
      className={`${overlayCls} ${modalBackdropCls}`}
      onClick={onCancel}
    >
      <div
        className={`w-full max-w-sm p-5 ${modalCardCls}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-center text-base font-semibold text-slate-900">
          {title}
        </h2>
        {body && (
          <p className="mt-2 whitespace-pre-line text-center text-sm text-slate-600">{body}</p>
        )}
        <div className="mt-5 flex justify-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors ${
              danger ? 'bg-rose-600 hover:bg-rose-500' : 'bg-blue-600 hover:bg-blue-500'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
